import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner, Table, Modal, Badge, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getAllCompanies } from '../../api/companyApi';
import { schedulingApi } from '../../api/schedulingApi';

const GDScheduling = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [gdDate, setGdDate] = useState('');
  const [gdStartTime, setGdStartTime] = useState('');
  const [gdEndTime, setGdEndTime] = useState('');
  const [studentEmails, setStudentEmails] = useState('');
  const [loading, setLoading] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [forceSchedule, setForceSchedule] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const companies = await getAllCompanies();
      setCompanies(companies);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to fetch companies');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedCompany || !gdDate || !gdStartTime || !gdEndTime || !studentEmails.trim()) {
      toast.error('Please fill all required fields');
      return;
    }

    // Parse student emails
    const emailList = studentEmails
      .split(/[\n,;]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    if (emailList.length === 0) {
      toast.error('Please provide at least one student email');
      return;
    }

    setLoading(true);
    setConflicts([]);

    try {
      const requestData = {
        company_id: parseInt(selectedCompany),
        date_str: gdDate,
        gd_start_time: gdStartTime,
        gd_end_time: gdEndTime,
        student_emails: emailList,
        force_schedule: forceSchedule
      };

      const response = await schedulingApi.gdScheduling(requestData);
      
      if (response.status === 200) {
        toast.success(response.data.message);
        
        // Reset form
        setSelectedCompany('');
        setGdDate('');
        setGdStartTime('');
        setGdEndTime('');
        setStudentEmails('');
        setForceSchedule(false);
        
        if (response.data.errors && response.data.errors.length > 0) {
          toast.warning(`${response.data.errors.length} errors occurred during scheduling`);
          console.log('Scheduling errors:', response.data.errors);
        }
      }
    } catch (error) {
      if (error.response && error.response.status === 409) {
        // Check if it's a conflict or existing GD
        if (error.response.data.conflicts) {
          setConflicts(error.response.data.conflicts);
          setShowConflictModal(true);
        } else if (error.response.data.existing_gd_time) {
          // Handle existing GD case
          const confirmed = window.confirm(
            `${error.response.data.message}\n\nDo you want to reschedule the GD?`
          );
          if (confirmed) {
            setForceSchedule(true);
            // Retry with force schedule
            handleSubmit(e);
            return;
          }
        } else {
          toast.error(error.response.data.message);
        }
      } else {
        console.error('Error in GD scheduling:', error);
        const errorMessage = error.response?.data?.message || 'Failed to process GD scheduling';
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForceSchedule = async () => {
    setShowConflictModal(false);
    setForceSchedule(true);
    
    // Resubmit with force schedule
    const emailList = studentEmails
      .split(/[\n,;]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    const requestData = {
      company_id: parseInt(selectedCompany),
      date_str: gdDate,
      gd_start_time: gdStartTime,
      gd_end_time: gdEndTime,
      student_emails: emailList,
      force_schedule: true
    };

    setLoading(true);
    try {
      const response = await schedulingApi.gdScheduling(requestData);
      toast.success(response.data.message);
      
      // Reset form
      setSelectedCompany('');
      setGdDate('');
      setGdStartTime('');
      setGdEndTime('');
      setStudentEmails('');
      setForceSchedule(false);
    } catch (error) {
      console.error('Error in force GD scheduling:', error);
      const errorMessage = error.response?.data?.message || 'Failed to force schedule GD';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const calculateDuration = () => {
    if (gdStartTime && gdEndTime) {
      const start = new Date(`2000-01-01T${gdStartTime}:00`);
      const end = new Date(`2000-01-01T${gdEndTime}:00`);
      const diff = (end - start) / (1000 * 60); // minutes
      
      if (diff > 0) {
        const hours = Math.floor(diff / 60);
        const minutes = diff % 60;
        return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
      }
    }
    return '';
  };

  const getEmailCount = () => {
    const emailList = studentEmails
      .split(/[\n,;]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);
    return emailList.length;
  };

  return (
    <Container className="mt-4">
      <h2 className="mb-4">GD Scheduling</h2>
      
      <Card>
        <Card.Header>
          <h5>Schedule Group Discussion for Company</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="info">
            <strong>Group Discussion Scheduling:</strong>
            <ul className="mb-0 mt-2">
              <li>All selected students will be scheduled for GD at the same time</li>
              <li>Conflicting interviews will be shown for review</li>
              <li>Force schedule will make conflicting interviews "UNSCHEDULED"</li>
              <li>GD schedules are protected from regular scheduling</li>
            </ul>
          </Alert>

          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Company *</Form.Label>
                  <Form.Select
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                    required
                  >
                    <option value="">Select a company...</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>GD Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={gdDate}
                    onChange={(e) => setGdDate(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Time *</Form.Label>
                  <Form.Control
                    type="time"
                    value={gdStartTime}
                    onChange={(e) => setGdStartTime(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>End Time *</Form.Label>
                  <Form.Control
                    type="time"
                    value={gdEndTime}
                    onChange={(e) => setGdEndTime(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Duration</Form.Label>
                  <Form.Control
                    type="text"
                    value={calculateDuration()}
                    readOnly
                    placeholder="Auto-calculated"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>
                Student Emails * 
                {getEmailCount() > 0 && (
                  <Badge bg="secondary" className="ms-2">
                    {getEmailCount()} students
                  </Badge>
                )}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                value={studentEmails}
                onChange={(e) => setStudentEmails(e.target.value)}
                placeholder="Enter student emails (one per line, comma-separated, or semicolon-separated)&#10;Example:&#10;f20210001@pilani.bits-pilani.ac.in&#10;f20210002@pilani.bits-pilani.ac.in"
                required
              />
              <Form.Text className="text-muted">
                Enter student email addresses. You can separate them with new lines, commas, or semicolons.
              </Form.Text>
            </Form.Group>

            <div className="d-flex gap-2">
              <Button 
                type="submit" 
                variant="primary" 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Processing...
                  </>
                ) : (
                  'Schedule GD'
                )}
              </Button>

              <Button 
                type="button" 
                variant="secondary" 
                onClick={() => {
                  setSelectedCompany('');
                  setGdDate('');
                  setGdStartTime('');
                  setGdEndTime('');
                  setStudentEmails('');
                  setForceSchedule(false);
                }}
              >
                Reset
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Conflict Modal */}
      <Modal show={showConflictModal} onHide={() => setShowConflictModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>GD Scheduling Conflicts Detected</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <strong>Warning:</strong> Some students have conflicting interviews during the GD time slot. 
            Proceeding with force schedule will make these interviews "UNSCHEDULED".
          </Alert>
          
          {conflicts.length > 0 && (
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Email</th>
                  <th>Conflicting Company</th>
                  <th>Conflicting Time</th>
                </tr>
              </thead>
              <tbody>
                {conflicts.map((conflict, index) => (
                  <tr key={index}>
                    <td>{conflict.student_name}</td>
                    <td>{conflict.student_email}</td>
                    <td>{conflict.conflicting_company}</td>
                    <td>{conflict.conflicting_time}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConflictModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleForceSchedule}>
            Force Schedule GD
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default GDScheduling;